import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PosPage from './pages/PosPage';
import CashierPage from './pages/CashierPage';
import LoyaltyConfigPage from './pages/LoyaltyConfigPage';
import InventoryPage from './pages/InventoryPage';
import ReportsPage from './pages/ReportsPage';
import CreateProductPage from './pages/CreateProductPage';
import ExpiredProductsPage from './pages/ExpiredProductsPage';
import LowStocksPage from './pages/LowStocksPage';
import CategoryPage from './pages/CategoryPage';
import SubCategoryPage from './pages/SubCategoryPage';
import BrandsPage from './pages/BrandsPage';
import UnitsPage from './pages/UnitsPage';
import VariantAttributesPage from './pages/VariantAttributesPage';
import WarrantiesPage from './pages/WarrantiesPage';
import PrintBarcodePage from './pages/PrintBarcodePage';
import PrintQrCodePage from './pages/PrintQrCodePage';
import ManageStockPage from './pages/ManageStockPage';
import StockAdjustmentPage from './pages/StockAdjustmentPage';
import StockTransferPage from './pages/StockTransferPage';
import StaffManagementPage from './pages/StaffManagementPage';
import AppSettingsPage from './pages/AppSettingsPage';
import StoreBranchesPage from './pages/StoreBranchesPage';
import ProfilePage from './pages/ProfilePage';
import StoreFrontendPage from './pages/StoreFrontendPage';
import OrdersManagementPage from './pages/OrdersManagementPage';
import MasterLicencesPage from './pages/MasterLicencesPage';
import MasterControlCenterPage from './pages/MasterControlCenterPage';
import MasterBillingPage from './pages/MasterBillingPage';
import MasterMonitorPage from './pages/MasterMonitorPage';
import ForcePasswordChangePage from './pages/ForcePasswordChangePage';
import Layout from './components/Layout';

const FORCE_PW_PATH = '/admin/force-password-change';

function ProtectedRoute({ children }) {
    const { token, user } = useAuth();
    const location = useLocation();

    if (!token) {
        return <Navigate to="/admin/login" replace />;
    }

    if (user?.mustChangePassword && location.pathname !== FORCE_PW_PATH) {
        return <Navigate to={FORCE_PW_PATH} replace />;
    }

    return children;
}

function HomeRoute() {
    const { user, isMasterDeployment } = useAuth();
    if (!user) return <Navigate to="/admin/login" replace />;
    if (user.role === 'super_admin' && isMasterDeployment) return <Navigate to="/admin/master/control" replace />;
    if (['cashier', 'staff'].includes(user.role)) return <Navigate to="/admin/pos/cashier" replace />;
    return <DashboardPage />;
}

function RoleRoute({ children, allowedRoles }) {
    const { user, isMasterDeployment } = useAuth();
    if (!user) return <Navigate to="/admin/login" replace />;
    if (allowedRoles.includes('super_admin') && !isMasterDeployment) return <Navigate to="/admin" replace />;
    if (allowedRoles.includes(user.role)) return children;
    if (['cashier', 'staff'].includes(user.role)) return <Navigate to="/admin/pos/cashier" replace />;
    return <Navigate to="/admin" replace />;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<StoreFrontendPage />} />
            <Route path="/store/:storeId" element={<StoreFrontendPage />} />

            <Route path="/admin/login" element={<LoginPage />} />
            <Route path="/admin/force-password-change" element={<ForcePasswordChangePage />} />
            <Route path="/admin/register" element={<Navigate to="/admin/login" replace />} />
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />
            <Route path="/register" element={<Navigate to="/admin/login" replace />} />

            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<HomeRoute />} />
                <Route path="pos" element={<PosPage />} />
                <Route path="pos/cashier" element={<CashierPage />} />
                <Route path="pos/loyalty" element={<RoleRoute allowedRoles={['admin', 'manager']}><LoyaltyConfigPage /></RoleRoute>} />
                <Route path="inventory" element={<RoleRoute allowedRoles={['admin', 'manager']}><InventoryPage /></RoleRoute>} />
                <Route path="inventory/create" element={<RoleRoute allowedRoles={['admin', 'manager']}><CreateProductPage /></RoleRoute>} />
                <Route path="inventory/expired" element={<RoleRoute allowedRoles={['admin', 'manager']}><ExpiredProductsPage /></RoleRoute>} />
                <Route path="inventory/low-stock" element={<RoleRoute allowedRoles={['admin', 'manager']}><LowStocksPage /></RoleRoute>} />
                <Route path="inventory/category" element={<RoleRoute allowedRoles={['admin', 'manager']}><CategoryPage /></RoleRoute>} />
                <Route path="inventory/sub-category" element={<RoleRoute allowedRoles={['admin', 'manager']}><SubCategoryPage /></RoleRoute>} />
                <Route path="inventory/brands" element={<RoleRoute allowedRoles={['admin', 'manager']}><BrandsPage /></RoleRoute>} />
                <Route path="inventory/units" element={<RoleRoute allowedRoles={['admin', 'manager']}><UnitsPage /></RoleRoute>} />
                <Route path="inventory/variants" element={<RoleRoute allowedRoles={['admin', 'manager']}><VariantAttributesPage /></RoleRoute>} />
                <Route path="inventory/warranties" element={<RoleRoute allowedRoles={['admin', 'manager']}><WarrantiesPage /></RoleRoute>} />
                <Route path="inventory/barcode" element={<RoleRoute allowedRoles={['admin', 'manager']}><PrintBarcodePage /></RoleRoute>} />
                <Route path="inventory/qrcode" element={<RoleRoute allowedRoles={['admin', 'manager']}><PrintQrCodePage /></RoleRoute>} />
                <Route path="inventory/manage-stock" element={<RoleRoute allowedRoles={['admin', 'manager']}><ManageStockPage /></RoleRoute>} />
                <Route path="inventory/adjustment" element={<RoleRoute allowedRoles={['admin', 'manager']}><StockAdjustmentPage /></RoleRoute>} />
                <Route path="inventory/transfer" element={<RoleRoute allowedRoles={['admin', 'manager']}><StockTransferPage /></RoleRoute>} />
                <Route path="staff" element={<RoleRoute allowedRoles={['admin']}><StaffManagementPage /></RoleRoute>} />
                <Route path="stores" element={<RoleRoute allowedRoles={['admin', 'manager']}><StoreBranchesPage /></RoleRoute>} />
                <Route path="orders" element={<RoleRoute allowedRoles={['admin', 'manager']}><OrdersManagementPage /></RoleRoute>} />
                <Route path="master/control" element={<RoleRoute allowedRoles={['super_admin']}><MasterControlCenterPage /></RoleRoute>} />
                <Route path="master/monitor" element={<RoleRoute allowedRoles={['super_admin']}><MasterMonitorPage /></RoleRoute>} />
                <Route path="master/billing" element={<RoleRoute allowedRoles={['super_admin']}><MasterBillingPage /></RoleRoute>} />
                <Route path="master-licences" element={<RoleRoute allowedRoles={['super_admin']}><MasterLicencesPage /></RoleRoute>} />
                <Route path="master" element={<RoleRoute allowedRoles={['super_admin']}><Navigate to="/admin/master/control" replace /></RoleRoute>} />
                <Route path="settings" element={<RoleRoute allowedRoles={['admin', 'manager']}><AppSettingsPage /></RoleRoute>} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="reports" element={<RoleRoute allowedRoles={['admin', 'manager']}><ReportsPage /></RoleRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <AppRoutes />
        </AuthProvider>
    );
}
